import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const rowVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function ComparisonSection() {
  const comparisons = [{
    category: 'Talent quality',
    bpo: 'Hit or miss. Often unfamiliar with carrier portals and how benefits actually work.',
    mach: 'Highly skilled, US-based operations professionals who know enrollment processes inside and out.'
  }, {
    category: 'Ramp-up time',
    bpo: 'Takes months to train them on your specific carriers and systems.',
    mach: 'Ready in 24 hours. Just show us how you do it once, and our dedicated team takes it from there.'
  }, {
    category: 'Visibility',
    bpo: 'Hard to know what is actually getting done. You lose track of which groups are set up.',
    mach: 'You can easily check the status of every single enrollment we handle with your account manager.'
  }, {
    category: 'Cost efficiency',
    bpo: 'Expensive to hire more people for busy season, then let them go when it slows down.',
    mach: 'More reliable and cost-effective because our expert team scales with your needs.'
  }];
  return <section className="py-24 md:py-32 bg-slate-950 text-gray-50" data-id="element-11">
      <div className="max-w-6xl mx-auto px-6" data-id="element-12">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="mb-16 md:mb-24 text-center flex flex-col items-center" data-id="element-13">
          <span className="text-steel-400 font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-14">
            Mach vs. Traditional Outsourcing
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold max-w-3xl leading-tight" data-id="element-15">
            All the benefits of an outsourced team. None of the downsides.
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="w-full border-t border-slate-800/50" data-id="element-16">
          {/* Desktop Header Row */}
          <div className="hidden md:grid grid-cols-12 gap-6 py-6 border-b border-slate-800/50 text-sm tracking-wider uppercase font-semibold" data-id="element-17">
            <div className="col-span-3 text-gray-400/50" data-id="element-18">Category</div>
            <div className="col-span-4 text-gray-400/50" data-id="element-19">
              Traditional Outsourcing
            </div>
            <div className="col-span-5 text-steel-300 pl-6" data-id="element-20">Mach</div>
          </div>

          {/* Comparison Rows */}
          {comparisons.map((item, i) => <motion.div key={i} variants={rowVariants} className="group relative border-b border-slate-800/30" data-id="element-21">
              {/* Desktop Layout */}
              <div className="hidden md:grid grid-cols-12 gap-6 items-center py-8 relative z-10" data-id="element-22">
                <div className="col-span-3 font-medium text-gray-200 text-lg" data-id="element-23">
                  {item.category}
                </div>

                <div className="col-span-4 flex items-start gap-3 text-gray-300/50 pr-6" data-id="element-24">
                  <XCircleIcon className="w-5 h-5 mt-0.5 shrink-0 text-slate-600/50" data-id="element-25" />
                  <span className="leading-relaxed" data-id="element-26">{item.bpo}</span>
                </div>

                <div className="col-span-5 flex items-start gap-3 text-gray-50 pl-6 py-2" data-id="element-27">
                  <CheckCircleIcon className="w-5 h-5 mt-0.5 shrink-0 text-steel-400" data-id="element-28" />
                  <span className="font-medium text-lg leading-relaxed" data-id="element-29">
                    {item.mach}
                  </span>
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="md:hidden flex flex-col py-8 gap-6 relative z-10" data-id="element-30">
                <div className="font-medium text-gray-200 text-xl border-b border-slate-800/30 pb-4" data-id="element-31">
                  {item.category}
                </div>

                <div className="flex flex-col gap-2" data-id="element-32">
                  <div className="text-xs uppercase tracking-wider font-semibold text-gray-400/50 mb-1" data-id="element-33">
                    Traditional Outsourcing
                  </div>
                  <div className="flex items-start gap-3 text-gray-300/50" data-id="element-34">
                    <XCircleIcon className="w-5 h-5 mt-0.5 shrink-0 text-slate-600/50" data-id="element-35" />
                    <span className="leading-relaxed" data-id="element-36">{item.bpo}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-2" data-id="element-37">
                  <div className="text-xs uppercase tracking-wider font-semibold text-steel-300 mb-1" data-id="element-38">
                    Mach
                  </div>
                  <div className="flex items-start gap-3 text-gray-50" data-id="element-39">
                    <CheckCircleIcon className="w-5 h-5 mt-0.5 shrink-0 text-steel-400" data-id="element-40" />
                    <span className="font-medium text-lg leading-relaxed" data-id="element-41">
                      {item.mach}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mach Column Highlight Background (Desktop only) */}
              <div className="hidden md:block absolute top-0 bottom-0 right-0 w-[41.666667%] bg-steel-500/5 -z-0 rounded-lg transition-colors duration-300 group-hover:bg-steel-500/10" data-id="element-42" />
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}